<template>
  <div class="scrollable" ref="scrollable">
    <div class="tw-container min-h-screen" style="flex-grow: 1">
      <SimplifiedHeroNew
        :title="onboardingTextConfig.communitiesSimplifiedTitle"
        :subtitle="onboardingTextConfig.communitiesSimplifiedSubtitle"
      />
      <ModernFiltersBar
        :search-query="searchQuery"
        @update:search-query="searchQuery = $event"
        :placeholder="$t('community_search_placeholder')"
        @clear-search="clearSearch"
        @clear-all="clearAllFilters"
        :has-custom-filters="membershipFilter !== 'all'"
        :custom-filters-count="membershipFilter !== 'all' ? 1 : 0"
      >
        <template #filters>
          <!-- Membership Filter Group -->
          <div class="filter-group membership-group tw-flex justify-center align-center mx-auto">
            <label class="filter-label mr-2">{{ $t('community_filter_membership') }}</label>
            <div class="filter-buttons">
              <button
                @click="setMembershipFilter('all')"
                :class="['filter-btn', { active: membershipFilter === 'all' }]"
              >
                {{ $t('community_filter_all') }}
              </button>
              <button
                v-if="isLoggedIn"
                @click="setMembershipFilter('member')"
                :class="['filter-btn', { active: membershipFilter === 'member' }]"
              >
                {{ $t('community_filter_member') }}
              </button>
              <button
                v-if="isLoggedIn && (isOwner || isMember || isPortfolioMember)"
                @click="setMembershipFilter('manager')"
                :class="['filter-btn', { active: membershipFilter === 'manager' }]"
              >
                {{ $t('community_filter_manager') }}
              </button>
            </div>
          </div>
        </template>

        <template #active-filters>
          <!-- Membership Filter Tag -->
          <span
            v-if="membershipFilter !== 'all'"
            class="active-filter-tag"
            @click="setMembershipFilter('all')"
          >
            {{ $t('community_filter_membership') }}: {{ membershipFilter === 'member' ? $t('community_filter_member') : $t('community_filter_manager') }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </span>
        </template>
      </ModernFiltersBar>

      <div class="communities__card-container" id="introjs-all-communities">
        <div v-if="!loading && filteredCommunities.length > 0" class="row">
          <div class="col col-xs-12 col-sm-4" v-for="(community, index) in filteredCommunities" :key="index">
            <div style="width: 100%; height: calc(100% - 20px); padding: 0px 10px 10px 10px;">

              <simplified-image-card
                v-if="community.isAd && $store.getters.isEcAerospace"
                background-color="white"
                custom-background="white"
                class="community-simplified-image-card white-background"
                :name="community.name"
                :full-size="true"
                :image="community.image"
                :sub-title="community.description"
                :left-align-text="true"
                @click="openUrl(community.link)"
              />
              <simplified-image-card
                v-else-if="!community.isAd"
                background-color="white"
                class="community-simplified-image-card"
                :name="community.name"
                :full-size="true"
                :image="getImageForCommunity(community)"
                :sub-title="community.description"
                :link="'/communities/' + community.id"
                :left-align-text="true"
                :tag-icons="getCommunityIcons(community)"
                type="community"
              />
            </div>
          </div>
        </div>
        <!-- No Search Results -->
        <div v-else-if="!loading && searchQuery && filteredCommunities.length === 0"
             class="text-center py-12">
          <h2 class="text-2xl text-gray-600 mb-4">No communities found</h2>
          <p class="text-gray-500">Try adjusting your search query</p>
        </div>
        <div v-else-if="loading">
          <Loading/>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import SimplifiedHeroNew from '../components/SimplifiedHero/SimplifiedHeroNew.vue'
  import Container from '../components/Container/Container.vue'
  import SimplifiedImageCard from '../components/Connection/SimplifiedImageCard.vue'
  import CommunityCard from '../components/Simplified/CommunityCard.vue'
  import Loading from '../components/Dashboard/ConceptMap/Loading.vue'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '../store/modules/communities.js'
  import OnboardingMixin from '@/util/OnboardingMixin'
  import { getImageForCommunity, truncate } from '@/util/helpers.js'
  import IntroJsMixin from '@/util/IntroJsMixin'
  import { defineComponent } from 'vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import { Community } from '@/Domain/Community/types'
  import ModernFiltersBar from '../components/Filters/ModernFiltersBar.vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: 'Communities',
    components: {
      SimplifiedHeroNew,
      SimplifiedImageCard,
      Container,
      CommunityCard,
      Loading,
      ModernFiltersBar,
    },
    data() {
      return {
        searchQuery: '',
        membershipFilter: 'all', // 'all', 'member', 'manager'
      }
    },
    computed: {
      communities() {
        if (this.$store.getters.isEcAerospace) {
          return [
            this.$store.getters.isEcAerospace && {
              id: null,
              name: 'not an associate yet? Join AerospaceNZ',
              description: 'Join AerospaceNZ and connect with New Zealand’s aerospace community.',
              image: '/images/static-home-page/ec-health/aerospace-nz-logo-new.png',
              link: 'https://www.aerospace.org.nz/',
              isAd: true,
            },
            ...this.$store.state.communities.listData.data,
          ]
        }
        return this.$store.state.communities.listData.data
      },
      loading() {
        return this.$store.state.communities.listData.loading
      },
      isOwner() {
        return this.$store.getters.isOwner
      },
      isLoggedIn() {
        return this.$store.getters.isLoggedIn
      },
      userId() {
        return this.$store.getters.userId
      },
      filteredCommunities() {
        let filtered = this.communities

        // Apply membership filter
        if (this.membershipFilter === 'member') {
          filtered = filtered.filter(community => {
            if (community.isAd) return true // Always show ads
            return this.joinedCommunities?.filter((c) => c.id === community.id).length > 0
          })
        } else if (this.membershipFilter === 'manager') {
          filtered = filtered.filter(community => {
            if (community.isAd) return true // Always show ads
            return this.isCommunityManager(community)
          })
        }

        // Apply search filter
        if (this.searchQuery.trim()) {
          const query = this.searchQuery.toLowerCase()
          filtered = filtered.filter(community => {
            if (community.isAd) return true // Always show ads
            return community.name.toLowerCase().includes(query) ||
              (community.description && community.description.toLowerCase().includes(query))
          })
        }

        return filtered
      }
    },
    methods: {
      clearSearch() {
        this.searchQuery = ''
      },
      clearAllFilters() {
        this.searchQuery = ''
        this.membershipFilter = 'all'
      },
      setMembershipFilter(filter) {
        this.membershipFilter = filter
      },
      openUrl(url) {
        window.open(url, '_blank')
      },
      truncate,
      getImageForCommunity,
      getCommunityIcons(community: Community) {
        const icons: string[] = []

        if (this.isCommunityManager(community)) {
          icons.push("user-check")
        }

        if (this.joinedCommunities?.filter((c) => c.id === community.id).length > 0) {
          icons.push("users")
        }

        return icons
      },
      fetchCommunities() {
        this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITIES_LIST)
          .then(() => {
            // timeout without milliseconds to allow the community cards to render before starting the intro
            setTimeout(() => {
              this.startIntroJs()
              // wait for the simplified image card timeout
            }, 200)
          })
      },
      startIntroJs() {
        if (this.seenIntros.includes('communitiesOverviewPage') || !this.isLoggedIn) {
          return
        }
        const config = this.buildIntroJsConfig(this.config, this.$store.getters.userRole)
        if (!config || !config.communitiesOverviewPage || !config.communitiesOverviewPage.steps) {
          return
        }

        config.communitiesOverviewPage.steps = this.getIntroJsStepsConfig(config.communitiesOverviewPage.steps)
        const intro = this.introJs().setOptions(config.communitiesOverviewPage)
        this.currentIntro = intro

        const componentScope = this
        intro.onbeforechange(this.introJsBeforeStepCallback.bind(this))
        intro.onafterchange(this.introJsAfterStepCallback.bind(this))
        intro.oncomplete(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })
        intro.onexit(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })

        intro.start()
      },
    },
    mounted() {
      if (!this.$store.getters.hasAccessToCommunities) {
        this.$router.push('/')
      }

      this.fetchCommunities()
    },
    mixins: [OnboardingMixin, IntroJsMixin, CommunityMixin, UserMixin]
  })
</script>

<style lang="scss" scoped>
  .communities-container {
    height: 100%;
    overflow: auto;
    width: 100%;
    display: flex;
    flex-direction: column;

    .communities__card-container {
      margin-bottom: 50px;
    }
  }

  /* Membership Filter Styling */
  .membership-group {
    .filter-buttons {
      display: flex;
      gap: 0.25rem;
      border-radius: 6px;
      background: #f3f4f6;
      padding: 2px;

      .filter-btn {
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: #6b7280;
        background: transparent;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;

        &:hover {
          color: #374151;
        }

        &.active {
          background: white;
          color: var(--primary-community, #3b82f6);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
      }
    }
  }

  // Responsive design
  @media (max-width: 768px) {
    .membership-group {
      .filter-buttons {
        width: 100%;

        .filter-btn {
          flex: 1;
          text-align: center;
        }
      }
    }
  }

  .filter-label {
    font-weight: 500;
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-self: center;
  }

  /* Active filter tag styles for slotted content */
  .active-filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: var(--primary-community, #3b82f6);
    color: white;
    border: 1px solid var(--primary-community, #3b82f6);
    border-radius: 16px;
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: var(--primary-community-lighter, #dbeafe);
      border-color: var(--primary-community-lighter, #dbeafe);
      transform: translateY(-1px);
    }

    svg {
      width: 12px;
      height: 12px;
      opacity: 0.8;
    }

    &:hover svg {
      opacity: 1;
    }
  }
</style>
